import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createOrder, CreateOrderInput } from '../api/orders';
import { fetchDeliverers } from '../api/deliverers';
import { PageHeader } from '../components/ui/PageHeader';
import { OrderLocationMap } from '../components/OrderLocationMap';
import { createOrderSchema, validate } from '../lib/validation';
import { Avatar } from '../components/Avatar';

const PARIS: [number, number] = [48.8566, 2.3522];

const PAYMENT_METHODS: Array<{ value: CreateOrderInput['paymentMethod']; label: string }> = [
  { value: 'cash', label: 'Espèces' },
  { value: 'credit_card', label: 'Carte bancaire' },
  { value: 'mobile_money', label: 'Mobile money' },
  { value: 'bank_transfer', label: 'Virement' },
  { value: 'platform', label: 'Plateforme' }
];

export const CreateOrderPage = () => {
  const [activePoint, setActivePoint] = useState<'pickup' | 'delivery'>('pickup');
  const [form, setForm] = useState<CreateOrderInput>({
    delivererId: '',
    pickupAddress: '',
    pickupLatitude: PARIS[0],
    pickupLongitude: PARIS[1],
    deliveryAddress: '',
    deliveryLatitude: PARIS[0],
    deliveryLongitude: PARIS[1],
    recipientName: '',
    recipientPhone: '',
    senderName: '',
    senderPhone: ''
  });
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const autoDelivererDone = useRef(false);

  const deliverersQuery = useQuery({
    queryKey: ['deliverers'],
    queryFn: fetchDeliverers
  });

  const mutation = useMutation({
    mutationFn: createOrder,
    onSuccess: (res) => {
      setCreatedId(res.data.id);
    }
  });

  useEffect(() => {
    if (autoDelivererDone.current) return;
    const list = deliverersQuery.data?.data ?? [];
    if (list.length === 0) return;
    autoDelivererDone.current = true;
    setForm((f) => {
      if (f.delivererId.trim()) return f;
      const available = list.filter((d) => d.isAvailable);
      const pick = available[0] ?? list[0];
      return pick ? { ...f, delivererId: pick.id } : f;
    });
  }, [deliverersQuery.data]);

  const canSubmit = useMemo(() => {
    return (
      form.delivererId.trim() &&
      form.pickupAddress.trim() &&
      form.deliveryAddress.trim() &&
      form.recipientName.trim() &&
      form.recipientPhone.trim() &&
      form.senderName.trim() &&
      form.senderPhone.trim()
    );
  }, [form]);

  const update = <K extends keyof CreateOrderInput>(key: K, value: CreateOrderInput[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const delivererOptions = useMemo(() => {
    const rows = deliverersQuery.data?.data ?? [];
    return [...rows].sort((a, b) => Number(b.isAvailable) - Number(a.isAvailable));
  }, [deliverersQuery.data]);

  const markerPos: [number, number] =
    activePoint === 'pickup'
      ? [form.pickupLatitude, form.pickupLongitude]
      : [form.deliveryLatitude, form.deliveryLongitude];

  const onMapClick = (lat: number, lng: number) => {
    if (activePoint === 'pickup') {
      update('pickupLatitude', lat);
      update('pickupLongitude', lng);
    } else {
      update('deliveryLatitude', lat);
      update('deliveryLongitude', lng);
    }
  };

  const delivererLabel = (d: (typeof delivererOptions)[number]) => {
    const u = d.User;
    const name = u ? `${u.firstName} ${u.lastName}` : d.companyName ?? d.id.slice(0, 8);
    const suffix = d.isAvailable ? '' : ' (indisponible)';
    return `${name} · ${d.vehicleType}${suffix}`;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Créer une commande"
        description="Choix du livreur, carte pour les points collecte / livraison, puis expéditeur et destinataire."
      />

      <form
        className="card p-5 space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          setCreatedId(null);
          const result = validate(createOrderSchema, form);
          if (!result.ok) {
            setValidationErrors(result.errors);
            return;
          }
          setValidationErrors({});
          mutation.mutate(result.data as CreateOrderInput);
        }}
      >
        {Object.keys(validationErrors).length > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            <p className="font-medium">Corrigez les champs suivants :</p>
            <ul className="mt-1 ml-4 list-disc">
              {Object.entries(validationErrors).map(([k, msg]) => (
                <li key={k}>
                  <span className="font-mono text-xs">{k}</span> : {msg}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="space-y-1 md:col-span-2">
            <span className="text-sm font-medium text-slate-700">Livreur</span>
            {deliverersQuery.isLoading && <div className="text-sm text-slate-500">Chargement des livreurs…</div>}
            {deliverersQuery.isError && (
              <div className="text-sm text-red-600">Impossible de charger les livreurs (saisie UUID possible ci‑dessous).</div>
            )}
            <select
              className="input"
              value={form.delivererId}
              onChange={(e) => update('delivererId', e.target.value)}
              required
              disabled={deliverersQuery.isLoading}
            >
              <option value="">— Sélectionner —</option>
              {delivererOptions.map((d) => (
                <option key={d.id} value={d.id} disabled={!d.isAvailable}>
                  {delivererLabel(d)}
                </option>
              ))}
            </select>
            <input
              className="input mt-2 font-mono text-xs"
              value={form.delivererId}
              onChange={(e) => update('delivererId', e.target.value)}
              placeholder="UUID livreur (surcharge manuelle)"
              aria-label="UUID livreur"
            />
            {delivererOptions.length > 0 ? (
              <ul className="mt-3 max-h-56 space-y-2 overflow-y-auto rounded-lg border border-slate-100 p-2">
                {delivererOptions.map((d) => {
                  const selected = form.delivererId === d.id;
                  return (
                    <li key={d.id}>
                      <button
                        type="button"
                        disabled={!d.isAvailable}
                        onClick={() => update('delivererId', d.id)}
                        className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors ${
                          selected ? 'bg-brand-50 ring-1 ring-brand-200' : 'hover:bg-slate-50'
                        } ${!d.isAvailable ? 'cursor-not-allowed opacity-50' : ''}`}
                      >
                        <Avatar
                          size="sm"
                          profilePictureUrl={d.User?.profilePictureUrl}
                          firstName={d.User?.firstName}
                          lastName={d.User?.lastName}
                        />
                        <span className="text-sm font-medium text-slate-800">{delivererLabel(d)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </label>

          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Moyen de paiement</span>
            <select
              className="input"
              value={form.paymentMethod ?? ''}
              onChange={(e) => update('paymentMethod', (e.target.value || undefined) as CreateOrderInput['paymentMethod'])}
            >
              <option value="">(auto / défaut)</option>
              {PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="space-y-3">
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-sm">
            <button
              type="button"
              className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                activePoint === 'pickup' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
              onClick={() => setActivePoint('pickup')}
            >
              Point de collecte
            </button>
            <button
              type="button"
              className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                activePoint === 'delivery' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
              onClick={() => setActivePoint('delivery')}
            >
              Point de livraison
            </button>
          </div>

          <OrderLocationMap
            key={activePoint}
            center={markerPos}
            marker={markerPos}
            onMapClick={onMapClick}
            label={activePoint === 'pickup' ? 'Carte — collecte' : 'Carte — livraison'}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Adresse de collecte</span>
            <input
              className="input"
              value={form.pickupAddress}
              onChange={(e) => update('pickupAddress', e.target.value)}
              required
            />
          </label>
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Adresse de livraison</span>
            <input
              className="input"
              value={form.deliveryAddress}
              onChange={(e) => update('deliveryAddress', e.target.value)}
              required
            />
          </label>

          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Collecte (lat)</span>
            <input
              className="input"
              type="number"
              step="any"
              value={form.pickupLatitude}
              onChange={(e) => update('pickupLatitude', Number(e.target.value))}
              required
            />
          </label>
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Collecte (lng)</span>
            <input
              className="input"
              type="number"
              step="any"
              value={form.pickupLongitude}
              onChange={(e) => update('pickupLongitude', Number(e.target.value))}
              required
            />
          </label>
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Livraison (lat)</span>
            <input
              className="input"
              type="number"
              step="any"
              value={form.deliveryLatitude}
              onChange={(e) => update('deliveryLatitude', Number(e.target.value))}
              required
            />
          </label>
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Livraison (lng)</span>
            <input
              className="input"
              type="number"
              step="any"
              value={form.deliveryLongitude}
              onChange={(e) => update('deliveryLongitude', Number(e.target.value))}
              required
            />
          </label>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Destinataire</span>
            <input
              className="input"
              value={form.recipientName}
              onChange={(e) => update('recipientName', e.target.value)}
              required
            />
          </label>
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Téléphone destinataire</span>
            <input
              className="input"
              value={form.recipientPhone}
              onChange={(e) => update('recipientPhone', e.target.value)}
              required
            />
          </label>
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Expéditeur</span>
            <input
              className="input"
              value={form.senderName}
              onChange={(e) => update('senderName', e.target.value)}
              required
            />
          </label>
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Téléphone expéditeur</span>
            <input
              className="input"
              value={form.senderPhone}
              onChange={(e) => update('senderPhone', e.target.value)}
              required
            />
          </label>
        </div>

        <label className="space-y-1 block">
          <span className="text-sm font-medium text-slate-700">Notes</span>
          <textarea
            className="input min-h-[110px]"
            value={form.notes ?? ''}
            onChange={(e) => update('notes', e.target.value || undefined)}
            placeholder="Détails de livraison, code, instructions, etc."
          />
        </label>

        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={Boolean(form.urgency)}
            onChange={(e) => update('urgency', e.target.checked || undefined)}
          />
          Urgent
        </label>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button className="btn-primary" type="submit" disabled={!canSubmit || mutation.isPending}>
            {mutation.isPending ? 'Création…' : 'Créer'}
          </button>

          {mutation.isError && (
            <div className="text-sm text-red-600">Erreur: {(mutation.error as Error).message}</div>
          )}
          {mutation.isSuccess && createdId && (
            <div className="text-sm text-emerald-700">
              Commande créée: <span className="font-mono">{createdId}</span>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};
